/**
 * Human-readable labels for the enum-ish fields stored on property.
 * Centralized here so the home page, sidebar, edit form, and PDF binder
 * all show the same strings — earlier each surface had its own copy or
 * just showed the raw DB code (`pre60`, `dis`, `q`), which read like
 * developer artifacts to the user.
 *
 * Looking up an unknown code returns null; callers fall back to the raw
 * value or '—' as appropriate.
 */

import type {
  Basement,
  ExteriorMaterial,
  Foundation,
  Garage,
  HomeType,
  HvacType,
  LotBand,
  RoofMaterial,
  Sewer,
  WaterSource,
  YearBand,
} from '@/db/types';

export const HOME_TYPE_LABEL: Record<HomeType, string> = {
  house: 'Single-family house',
  town: 'Townhouse / row home',
  condo: 'Condo / apartment',
  mobile: 'Mobile / manufactured',
};

export const HVAC_LABEL: Record<HvacType, string> = {
  furnace: 'Forced-air furnace + AC',
  boiler: 'Boiler + radiators',
  heatpump: 'Heat pump',
  mini: 'Mini-split system',
  baseboard: 'Electric baseboard',
  radiant: 'Radiant floor heat',
  unsure: 'HVAC — to be determined',
};

export const LOT_LABEL: Record<LotBand, string> = {
  q: 'Under ¼ acre',
  '1': '¼ – 1 acre',
  '5': '1 – 5 acres',
  '20': '5 – 20 acres',
  '20+': '20+ acres',
};

export const YEAR_LABEL: Record<YearBand, string> = {
  pre60: 'Pre-1960',
  '60-80': '1960s–1980s',
  '90-00': '1990s–2000s',
  '2010+': '2010+',
  unsure: 'Year unknown',
};

export const BASEMENT_LABEL: Record<Basement, string> = {
  none: 'No basement',
  crawl: 'Crawl space',
  unfinished: 'Unfinished basement',
  finished: 'Finished basement',
  partial: 'Partial basement',
};

export const GARAGE_LABEL: Record<Garage, string> = {
  none: 'No garage',
  '1-car': '1-car garage',
  '2-car': '2-car garage',
  '3-car': '3-car garage',
  detached: 'Detached garage',
  carport: 'Carport',
};

export const WATER_LABEL: Record<WaterSource, string> = {
  city: 'City water',
  well: 'Private well',
  mixed: 'Mixed (city + well)',
  unsure: 'Water source unknown',
};

export const SEWER_LABEL: Record<Sewer, string> = {
  city: 'City sewer',
  septic: 'Septic system',
  shared: 'Shared system',
  unsure: 'Sewer unknown',
};

export const ROOF_LABEL: Record<RoofMaterial, string> = {
  asphalt: 'Asphalt shingle',
  metal: 'Metal',
  tile: 'Tile',
  slate: 'Slate',
  wood: 'Wood shake',
  flat: 'Flat / membrane',
  unsure: 'Roof material unknown',
};

export const FOUNDATION_LABEL: Record<Foundation, string> = {
  slab: 'Slab',
  crawl: 'Crawl space',
  basement: 'Basement',
  pier: 'Pier & beam',
  unsure: 'Foundation unknown',
};

export const EXTERIOR_LABEL: Record<ExteriorMaterial, string> = {
  vinyl: 'Vinyl siding',
  brick: 'Brick',
  wood: 'Wood',
  stucco: 'Stucco',
  'fiber-cement': 'Fiber cement',
  stone: 'Stone',
  aluminum: 'Aluminum siding',
  log: 'Log',
  other: 'Other',
};
